import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Play, Music } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import type { Playlist } from '@/services/spotifyService';
import { openSpotify } from '@/services/spotifyService';
import SpotifyPreviewPlayer from './SpotifyPreviewPlayer';

interface PlaylistCardProps extends Playlist {
  className?: string;
  isPreselected?: boolean;
}

const PlaylistCard: React.FC<PlaylistCardProps> = ({ isPreselected = false, ...playlist }) => {
  const { resolvedTheme } = useTheme();
  const isMobile = useIsMobile();
  const [previewOpen, setPreviewOpen] = useState(false);
  
  const textColor = resolvedTheme === 'dark' ? 'text-white' : 'text-black';
  const mutedTextColor = resolvedTheme === 'dark' ? 'text-white/70' : 'text-black/70';
  const hoverBg = resolvedTheme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-black/5';
  const activeBg = resolvedTheme === 'dark' ? 'bg-white/5' : 'bg-black/5';
  
  const isActive = isMobile && isPreselected;

  const hasPreviewTracks = playlist.tracks?.some(track => track.previewUrl);

  const handlePlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (hasPreviewTracks) {
      setPreviewOpen(true);
    } else {
      openSpotify(playlist.spotifyUrl);
    }
  };

  const handleCardClick = () => {
    if (hasPreviewTracks) {
      setPreviewOpen(true);
    } else {
      window.open(playlist.spotifyUrl, '_blank');
    }
  };

  return (
    <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
      <DialogTrigger asChild>
        <div
          onClick={handleCardClick}
          className={cn(
            'group flex flex-col cursor-pointer transition-all duration-200 p-3 rounded-lg overflow-hidden',
            hoverBg,
            isActive && activeBg,
            playlist.className
          )}
        >
          <div className="relative aspect-square rounded-md overflow-hidden mb-3 flex-none">
            <img 
              src={playlist.imageUrl} 
              alt={playlist.name}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" 
            />
            <div className={cn("absolute inset-0 bg-black/20 transition-opacity duration-200", 
              isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"
            )} />
            <button
              onClick={handlePlayClick}
              className={cn(
                "absolute bottom-2 right-2 w-12 h-12 bg-green-600 hover:bg-green-700 text-white rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 shadow-lg",
                isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"
              )}
              aria-label={`${hasPreviewTracks ? 'Preview' : 'Play'} ${playlist.name}`}
            >
              {hasPreviewTracks ? (
                <Music className="w-5 h-5" />
              ) : (
                <Play className="w-5 h-5 ml-0.5" />
              )}
            </button>
          </div>
          
          <div className="flex-1 flex flex-col justify-between min-h-0">
            <div>
              <h3 className={cn('font-medium text-sm line-clamp-2 mb-1', textColor)}>
                {playlist.name}
              </h3>
              <p className={cn('text-xs mb-2', mutedTextColor)}>
                {playlist.description}
              </p>
            </div>
            <div className={cn('text-xs flex items-center gap-2 flex-wrap', mutedTextColor)}>
              <span>{playlist.songs} songs</span>
              <span>•</span>
              <span>{playlist.duration}</span>
              {hasPreviewTracks && (
                <>
                  <span>•</span>
                  <span className="text-green-600 dark:text-green-400">Preview available</span>
                </>
              )}
            </div>
          </div>
        </div>
      </DialogTrigger>
      
      <DialogContent className="sm:max-w-md">
        <SpotifyPreviewPlayer 
          playlist={playlist} 
          onClose={() => setPreviewOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
};

export default PlaylistCard;